"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useEffectEvent, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Image from "next/image";
import { getVideoEmbedSrc, VIDEO_EMBED_IFRAME_ALLOW } from "@repo/sanity-utils/video-embed";
import { X } from "lucide-react";
import playIcon from "./playIcon.svg";
import { MUTED_PREVIEW_VISIBILITY_RATIO, shouldStartMutedPreview, toYouTubeNoCookieEmbedUrl, withEmbedApiParams, withEmbedAutoplay, withEmbedPreviewParams } from "./videoEmbedPlayback";
import styles from "./DiscoveryMediaBlock.module.css";
type DiscoveryMediaPlayerProps = {
  videoUrl: string;
  title?: string;
};

/**
 * Video with a muted inline preview and a play overlay.
 *
 * The preview stays paused until the frame is actually on screen, then
 * latches so scrolling away does not restart the beginning. Opening the
 * lightbox must start unmuted audio inside the same tap: iOS will not unmute
 * a freshly mounted player after React state updates. We keep one
 * iframe/video element, assign the unmuted embed URL (or clear `muted`)
 * synchronously in the click handler, then portal that same node into a
 * body-level lightbox.
 */
export function DiscoveryMediaPlayer(t0) {
  const $ = _c(40);
  const {
    videoUrl,
    title: t1
  } = t0;
  const title = t1 === undefined ? "Embedded video" : t1;
  const [isOpen, setIsOpen] = useState(false);
  const [slotHost, setSlotHost] = useState(null);
  const [lightboxHost, setLightboxHost] = useState(null);
  const [embedPlayerSrc, setEmbedPlayerSrc] = useState(null);
  const [previewStarted, setPreviewStarted] = useState(false);
  const iframeRef = useRef(null);
  const videoRef = useRef(null);
  const frameRef = useRef(null);
  let embedSrc;
  let previewSrc;
  let stillSrc;
  let t2;
  if ($[0] !== videoUrl) {
    embedSrc = getVideoEmbedSrc(videoUrl);
    const apiSrc = embedSrc ? withEmbedApiParams(toYouTubeNoCookieEmbedUrl(embedSrc)) : null;
    previewSrc = apiSrc ? withEmbedPreviewParams(apiSrc) : null;
    stillSrc = apiSrc ? withEmbedAutoplay(apiSrc, {
      muted: true,
      autoplay: false
    }) : null;
    t2 = apiSrc ? withEmbedAutoplay(apiSrc) : null;
    $[0] = videoUrl;
    $[1] = embedSrc;
    $[2] = previewSrc;
    $[3] = stillSrc;
    $[4] = t2;
  } else {
    embedSrc = $[1];
    previewSrc = $[2];
    stillSrc = $[3];
    t2 = $[4];
  }
  const modalSrc = t2;
  const inlineEmbedSrc = previewStarted ? previewSrc : stillSrc;
  const activeEmbedSrc = embedPlayerSrc ?? inlineEmbedSrc;
  const portalTarget = isOpen && lightboxHost ? lightboxHost : slotHost;
  let t3;
  if ($[5] !== previewSrc) {
    t3 = function closeLightbox() {
      if (previewSrc && iframeRef.current) {
        iframeRef.current.src = previewSrc;
      }
      setEmbedPlayerSrc(null);
      setIsOpen(false);
    };
    $[5] = previewSrc;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const closeLightbox = t3;
  let t4;
  if ($[7] !== previewSrc) {
    t4 = () => {
      if (previewSrc && iframeRef.current) {
        iframeRef.current.src = previewSrc;
      }
      setEmbedPlayerSrc(null);
      setIsOpen(false);
    };
    $[7] = previewSrc;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const closeLightboxFromEffect = useEffectEvent(t4);
  let t5;
  if ($[9] !== modalSrc) {
    t5 = function openLightbox() {
      setPreviewStarted(true);
      if (modalSrc && iframeRef.current) {
        iframeRef.current.src = modalSrc;
        setEmbedPlayerSrc(modalSrc);
      } else {
        const node = videoRef.current;
        if (node) {
          node.muted = false;
          node.play().catch(_temp);
        }
      }
      setIsOpen(true);
    };
    $[9] = modalSrc;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const openLightbox = t5;
  let t6;
  let t7;
  if ($[11] !== previewStarted) {
    t6 = () => {
      if (previewStarted) {
        return;
      }
      const node_0 = frameRef.current;
      if (!node_0 || typeof IntersectionObserver === "undefined") {
        setPreviewStarted(true);
        return;
      }
      const observer = new IntersectionObserver(entries => {
        if (entries.some(shouldStartMutedPreview)) {
          setPreviewStarted(true);
        }
      }, {
        threshold: [0, MUTED_PREVIEW_VISIBILITY_RATIO, 1]
      });
      observer.observe(node_0);
      return () => observer.disconnect();
    };
    t7 = [previewStarted];
    $[11] = previewStarted;
    $[12] = t6;
    $[13] = t7;
  } else {
    t6 = $[12];
    t7 = $[13];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[14] !== embedSrc || $[15] !== isOpen || $[16] !== previewStarted) {
    t8 = () => {
      if (!previewStarted || embedSrc || isOpen) {
        return;
      }
      const node_1 = videoRef.current;
      if (!node_1) {
        return;
      }
      node_1.play().catch(_temp2);
    };
    t9 = [previewStarted, embedSrc, isOpen];
    $[14] = embedSrc;
    $[15] = isOpen;
    $[16] = previewStarted;
    $[17] = t8;
    $[18] = t9;
  } else {
    t8 = $[17];
    t9 = $[18];
  }
  useEffect(t8, t9);
  let t10;
  if ($[19] !== closeLightboxFromEffect || $[20] !== isOpen) {
    t10 = () => {
      if (!isOpen) {
        return;
      }
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      const onKeyDown = event => {
        if (event.key === "Escape") {
          closeLightboxFromEffect();
        }
      };
      document.addEventListener("keydown", onKeyDown);
      return () => {
        document.body.style.overflow = previousOverflow;
        document.removeEventListener("keydown", onKeyDown);
      };
    };
    $[19] = closeLightboxFromEffect;
    $[20] = isOpen;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== isOpen) {
    t11 = [isOpen];
    $[22] = isOpen;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  useEffect(t10, t11);
  let t12;
  if ($[24] !== activeEmbedSrc || $[25] !== isOpen || $[26] !== title || $[27] !== videoUrl) {
    t12 = activeEmbedSrc ? <iframe ref={iframeRef} src={activeEmbedSrc} title={title} allow={VIDEO_EMBED_IFRAME_ALLOW} allowFullScreen={isOpen} className={isOpen ? `${styles.mediaAsset} ${styles.mediaAssetLightbox}` : styles.mediaAsset} tabIndex={isOpen ? 0 : -1} /> : <video ref={videoRef} src={videoUrl} muted={!isOpen} playsInline={true} preload="metadata" controls={isOpen} className={isOpen ? `${styles.mediaAsset} ${styles.mediaAssetLightbox}` : styles.mediaAsset} />;
    $[24] = activeEmbedSrc;
    $[25] = isOpen;
    $[26] = title;
    $[27] = videoUrl;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const media = t12;
  let t13;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div ref={setSlotHost} className={styles.mediaSlot} />;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== media || $[31] !== portalTarget) {
    t14 = portalTarget ? createPortal(media, portalTarget) : null;
    $[30] = media;
    $[31] = portalTarget;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== closeLightbox || $[34] !== isOpen || $[35] !== openLightbox) {
    t15 = !isOpen ? <><div className={styles.embedShield} aria-hidden={true} /><div className={styles.hoverScrim} aria-hidden={true} /><button type="button" className={styles.playOverlay} onClick={openLightbox} aria-label="Play video"><span className={styles.playButton}><Image src={playIcon} alt="" width={59} height={59} className={styles.playIcon} /></span></button></> : createPortal(<><button type="button" className={styles.lightboxBackdrop} aria-label="Close video" onClick={closeLightbox} /><div ref={setLightboxHost} className={styles.lightboxMediaHost} /><button type="button" className={styles.lightboxClose} onClick={closeLightbox} aria-label="Close video"><X size={24} aria-hidden={true} /></button></>, document.body);
    $[33] = closeLightbox;
    $[34] = isOpen;
    $[35] = openLightbox;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== t14 || $[38] !== t15) {
    t16 = <div ref={frameRef} className={styles.mediaFrame}>{t13}{t14}{t15}</div>;
    $[37] = t14;
    $[38] = t15;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  return t16;
}
function _temp2() {}
function _temp() {}